import { type DefaultError } from '@tanstack/react-query';

/**
 * Wraps a promise to avoid try/catch blocks by returning a tuple [error, data].
 * Optimized for TanStack Query's `queryClient.fetchQuery` or `mutation.mutateAsync` usage.
 *
 * @param promise - The promise to wrap (typically from queryClient.fetchQuery)
 * @returns A promise that resolves to [error, data] tuple
 *
 * @example
 * ```typescript
 * const [error, data] = await safeAsync(queryClient.fetchQuery(getClusterQuery));
 * if (error) {
 *   console.error('Query failed:', error);
 *   return;
 * }
 * console.log('Success:', data);
 * ```
 *
 * @example
 * ```typescript
 * // E is inferred as Error from the mapper's return type
 * const [error] = await safeAsync(oneTrust.promise, (e: CustomError) => e);
 * ```
 */
export async function safeAsync<T, E = DefaultError>(
  promise: Promise<T>,
  // oxlint-disable-next-line typescript/no-explicit-any
  errorMapper?: (error: any) => E,
): Promise<[E, undefined] | [null, T]> {
  try {
    const data = await promise;
    return [null, data];
  } catch (error) {
    return [errorMapper ? errorMapper(error) : (error as E), undefined];
  }
}
